/**
 * Laufzettel-Barcode der B&B-Produktion (R43, R73; Schnittstelle docs/storeos/schnittstelle-bb-laufzettel.md).
 *
 *   LZ-<Bestellnummer>-<Position>            z. B. LZ-71234-2     Position mit Menge 1
 *   LZ-<Bestellnummer>-<Position>-<Stueck>   z. B. LZ-71234-2-1   Position mit Menge > 1: je Stueck ein Laufzettel (N6)
 *
 * Bestellnummer = Woo-Bestellnummer (ohne #), Position = laufende Nummer der Bestellposition in StoreOS
 * (1..n in Woo-Reihenfolge, wie `order_items.position`), Stueck = 1..Menge. Bei Menge 1 gilt `…-<Position>-1` als
 * gleichwertig zu `…-<Position>`. Gelesen wird tolerant: Gross-/Kleinschreibung egal, Leerzeichen am Rand egal,
 * fuehrende Nullen egal, und Handscanner mit US-Layout an deutscher Tastatur tippen `-` als `ß` bzw. `?`: beides gilt
 * als Bindestrich.
 */
export function laufzettelCode(bestellnummer: string, position: number, stueck?: number | null): string {
  return stueck ? `LZ-${bestellnummer}-${position}-${stueck}` : `LZ-${bestellnummer}-${position}`;
}

/** Alle Laufzettel einer Position: Menge 1 → ein Code ohne Stueck, Menge n > 1 → n Codes mit Stueck 1..n. */
export function laufzettelCodes(bestellnummer: string, position: number, menge: number): string[] {
  if (!(menge > 1)) return [laufzettelCode(bestellnummer, position)];
  return Array.from({ length: menge }, (_, i) => laufzettelCode(bestellnummer, position, i + 1));
}

export interface LaufzettelLesart { bestellnummer: string; position: number; stueck: number | null }

/**
 * Liest einen Laufzettel. `lesarten` ist nach Vorrang sortiert: zuerst mit Stueck (letzte Zahl), dann – fuer eine
 * Bestellnummer mit Bindestrich – ohne Stueck. Der Aufrufer nimmt die erste Lesart, zu der es eine Position gibt.
 */
export function parseLaufzettel(code: string): (LaufzettelLesart & { code: string; lesarten: LaufzettelLesart[] }) | null {
  // Erst ß ersetzen: 'ß'.toUpperCase() ist 'SS'.
  const t = (code ?? '').trim().replace(/[ßẞ?]/g, '-').toUpperCase();
  const lesarten: LaufzettelLesart[] = [];
  const mit = /^LZ-(.+?)-(\d{1,3})-(\d{1,3})$/.exec(t);
  if (mit && mit[1]) lesarten.push({ bestellnummer: mit[1], position: Number(mit[2]), stueck: Number(mit[3]) });
  const ohne = /^LZ-(.+)-(\d{1,3})$/.exec(t);
  if (ohne && ohne[1]) lesarten.push({ bestellnummer: ohne[1], position: Number(ohne[2]), stueck: null });
  const l = lesarten[0];
  if (!l) return null;
  return { ...l, code: laufzettelCode(l.bestellnummer, l.position, l.stueck), lesarten };
}
